import { isApiError } from '@amarantos/api-client'
import * as Sentry from '@sentry/react'
import i18n from 'i18next'

interface ValidationErrorPayload {
	message?: string
	details?: Record<string, string[]>
}

interface MessagePayload {
	message?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isMessagePayload(value: unknown): value is MessagePayload {
	return isRecord(value) && (value.message === undefined || typeof value.message === 'string')
}

function isValidationErrorPayload(value: unknown): value is ValidationErrorPayload {
	if (!isRecord(value)) {
		return false
	}

	if (value.message !== undefined && typeof value.message !== 'string') {
		return false
	}

	if (value.details === undefined) {
		return true
	}

	if (!isRecord(value.details)) {
		return false
	}

	return Object.values(value.details).every(
		(messages) => Array.isArray(messages) && messages.every((message) => typeof message === 'string'),
	)
}

export function isExpectedApiError(error: unknown): boolean {
	if (!isApiError(error) || !error.response) {
		return false
	}
	return error.response.status >= 400 && error.response.status < 500
}

export function reportErrorToSentry(error: unknown, componentStack?: string): void {
	if (!isExpectedApiError(error)) {
		Sentry.captureException(error, {
			contexts: {
				react: { componentStack },
			},
		})
	}
}

export function isNotFoundError(error: unknown): boolean {
	return isApiError(error) && error.response?.status === 404
}

/**
 * Only the API's own `message` is shown: it is localized server-side. Everything else axios
 * offers — its `Error.message`, the HTTP status text, a proxy's HTML body — is English, so
 * those cases take the caller's translated fallback instead.
 */
export function getApiErrorMessage(error: unknown, fallback: string = i18n.t('error.generic')): string {
	if (!isApiError(error) || !error.response) {
		return fallback
	}

	const { data } = error.response

	return isMessagePayload(data) && data.message ? data.message : fallback
}

export function getValidationDetails(error: unknown): Record<string, string[]> | null {
	if (!isApiError(error) || !error.response) {
		return null
	}

	if (!isValidationErrorPayload(error.response.data)) {
		return null
	}

	return error.response.data.details ?? null
}
